import { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGlobal } from '../contexts/GlobalContext';
import { useUser } from '../contexts/UserContext';
import { getBillTerm } from '../redux/actions/billTerm';
import { getItem } from '../redux/actions/item';
import { getDaybook } from '../redux/actions/daybook';

const useCalcTaxAmount = () => {
   const [data, setData] = useState({});
   const [header, setHeader] = useState({});
   const [lineItem, setLineItem] = useState([]);
   const [billTerm, setBillTerm] = useState(0);
   const [totalTaxableAmount, setTotalTaxableAmount] = useState(0);
   const [finalNetAmount, setFinalNetAmount] = useState(0);
   const [totals, setTotals] = useState({
      qty1: 0,
      qty2: 0,
      qty3: 0,
      basicAmount: 0,
      discount1: 0,
      discount2: 0,
      discount3: 0,
      netAmount: 0,
      taxAmount: 0,
      gstAmount: 0,
      taxableAmount: 0,
      netGstAmount: 0,
   });

   const { company } = useGlobal();
   const { userGlobalSettingsData } = useGlobal();
   const isDaybook = useMemo(() => userGlobalSettingsData?.[0]?.askCompany?.daybook || false, [userGlobalSettingsData]);
   const isItem = useMemo(() => userGlobalSettingsData?.[0]?.askCompany?.item || false, [userGlobalSettingsData]);
   const { user } = useUser();
   const dispatch = useDispatch();

   const itemData = useSelector((state) => state.item?.list);
   const billTermData = useSelector((state) => state.billTerm?.list);
   const daybookData = useSelector((state) => state.daybook?.list);

   const totalsAmount = useCallback(() => {
      setTotals({
         qty1: lineItem?.reduce((acc, item) => acc + Number(item?.qty1), 0),
         qty2: lineItem?.reduce((acc, item) => acc + Number(item?.qty2), 0),
         qty3: lineItem?.reduce((acc, item) => acc + Number(item?.qty3), 0),
         basicAmount: lineItem?.reduce((acc, item) => {
            return acc + Number(item?.amount || 0);
         }, 0),
         discount1: lineItem?.reduce((acc, item) => acc + Number(item?.addLess1Amount || 0), 0),
         discount2: lineItem?.reduce((acc, item) => acc + Number(item?.addLess2Amount || 0), 0),
         discount3: lineItem?.reduce((acc, item) => acc + Number(item?.addLess3Amount || 0), 0),
         netAmount: lineItem?.reduce((acc, item) => {
            return acc + Number(item?.netAmount || 0);
         }, 0),
         taxAmount: lineItem?.reduce((acc, item) => acc + Number(item?.taxAmount || 0), 0),
         gstAmount: lineItem?.reduce((acc, item) => acc + Number(item?.gstAmount || 0), 0),
         netGstAmount: lineItem?.reduce((acc, item) => acc + Number(item?.netGst || 0), 0),
         taxableAmount: lineItem?.reduce((acc, item) => acc + Number(item?.taxableAmount || 0), 0),
      });
      setFinalNetAmount(totals.taxableAmount + totals.netGstAmount);
   }, [lineItem, totals.netGstAmount, totals.taxableAmount]);

   const handleLineItem = useCallback(() => {
      const lineItem = data?.lineItem?.map((item, idx) => {
         const { itemRate, qty1, addLess1Percent = 0, addLess2Percent = 0, addLess3Percent = 0 } = item;

         let amount = itemRate * qty1;
         let netGst = 0;
         let gstPercent = 0;
         let subHSNId = null;
         let addLess1Amount = (amount * addLess1Percent) / 100;
         let base2 = amount - addLess1Amount;
         let addLess2Amount = (base2 * addLess2Percent) / 100;
         let base3 = base2 - addLess2Amount;
         let addLess3Amount = (base3 * addLess3Percent) / 100;

         let netAmount = +(amount - addLess1Amount - addLess2Amount - addLess3Amount).toFixed(2);

         let taxableAmount = (totalTaxableAmount * netAmount) / totals.netAmount;

         // ====================================================
         // GST Calculation
         // ====================================================

         const daybook = daybookData.find((daybook) => daybook?.id === data?.header?.daybookId);

         const itemInfo = itemData.find((i) => i?.id === item?.itemId);

         if (company?.setting?.applyGST && daybook?.applyGst) {
            let hsn;

            if (item.typeOfSupply?.toUpperCase() === 'GOODS') {
               if (itemInfo?.hsnData?.subHsn.length === 1) {
                  hsn = itemInfo?.hsnData?.subHsn[0];
               } else {
                  hsn = itemInfo?.hsnData?.subHsn.find(
                     (hsn) => itemInfo.itemRate >= hsn.fromRate && lineItem.itemRate <= hsn.toRate,
                  );
               }
            }
            if (item.typeOfSupply?.toUpperCase() === 'SERVICE') {
               if (itemInfo?.hsnData?.subHsn.length === 1) {
                  hsn = itemInfo?.hsnData?.subHsn[0];
               } else {
                  hsn = itemInfo?.hsnData?.subHsn.find(
                     (hsn) => itemInfo.itemRate >= hsn.fromRate && lineItem.itemRate <= hsn.toRate,
                  );
               }
            }
            gstPercent = hsn?.gst ? Number(hsn?.gst) : 0;
            netGst = itemRate * qty1 * (hsn?.gst / 100);
            subHSNId = hsn?.id;
         }

         // ====================================================
         // GST Calculation
         // ====================================================

         return {
            ...item,
            amount: Number(amount.toFixed(2)),
            netAmount: Number(netAmount.toFixed(2)),
            taxableAmount: Number(taxableAmount.toFixed(2)),
            addLess1Amount: Number(addLess1Amount.toFixed(2)),
            addLess2Amount: Number(addLess2Amount.toFixed(2)),
            addLess3Amount: Number(addLess3Amount.toFixed(2)),
            gstPercent: gstPercent,
            netGst: Number(netGst.toFixed(2)),
            subHSNId: subHSNId,
            typeOfSupply:
               item.typeOfSupply?.toUpperCase() === 'GOODS'
                  ? 1
                  : item.typeOfSupply?.toUpperCase() === 'SERVICE'
                     ? 2
                     : 3,
         };
      });
      setLineItem(lineItem);
   }, [
      company?.setting?.applyGST,
      data?.header?.daybookId,
      data?.lineItem,
      daybookData,
      itemData,
      totalTaxableAmount,
      totals.netAmount,
   ]);

   const setHeaderData = useCallback(async () => {
      setHeader((prev) => ({
         ...prev,
         ...data.header,
         basicAmount: Number(totals.netAmount),
         netAmount: Number(finalNetAmount),
         taxableAmount: totals.taxableAmount ? totals.taxableAmount : 0,
      }));
   }, [data.header, finalNetAmount, totals.netAmount, totals.taxableAmount]);

   const getData = useCallback(async () => {
      if (data?.header?.daybookId) {
         await dispatch(getBillTerm({ daybookId: data.header.daybookId }));
      }
      if (user && user?.loginhistory.companyId) {
         await dispatch(
            getItem({
               companyId: user?.loginhistory.companyId,
               ...(isItem && { companyIds: Number(user?.loginhistory?.companyId) }),
               sortBy: 'name',
               sortOrder: 'asc',
            }),
         );
         await dispatch(getDaybook({
            companyId: Number(user?.loginhistory?.companyId),
            ...(isDaybook && { companyIds: Number(user?.loginhistory?.companyId) })
         }));
      }
   }, [data?.header?.daybookId, dispatch, user]);

   useEffect(() => {
      let additionTotal = 0;
      let deductionTotal = 0;

      data.saleBillTerms?.forEach((term, index) => {
         const amount = parseFloat(term.amount) || 0;
         const termData = billTermData[index];

         switch (termData?.signname) {
            case 'Subtraction':
               deductionTotal += Math.abs(amount);
               break;
            case 'Addition':
               additionTotal += Math.abs(amount);
               break;
            case 'Both':
               if (amount < 0) {
                  deductionTotal += Math.abs(amount);
               } else {
                  additionTotal += amount;
               }
               break;
            default:
               // Handle other cases if needed
               break;
         }
      });

      let billTerm = additionTotal - deductionTotal;
      let netTotal = totals.netAmount + additionTotal;
      netTotal = netTotal - deductionTotal;

      setBillTerm(billTerm);
      setTotalTaxableAmount(netTotal);

      setHeader((prev) => ({
         ...prev,
         additionAmount: additionTotal.toFixed(2),
         deductionAmount: deductionTotal.toFixed(),
      }));

      return () => { };
   }, [billTermData.length, data.saleBillTerms, totals.netAmount]);

   useEffect(() => {
      totalsAmount();
      return () => { };
   }, [totalsAmount]);

   useEffect(() => {
      setTotalTaxableAmount(totals.netAmount + Number(billTerm));
      return () => { };
   }, [billTerm, totals.netAmount]);

   useEffect(() => {
      handleLineItem();
      return () => { };
   }, [handleLineItem]);

   useEffect(() => {
      getData();
      return () => { };
   }, [getData]);

   useEffect(() => {
      setHeaderData();
      return () => { };
   }, [setHeaderData]);

   return { data: { header: header, lineItem: lineItem, billTerm: data.saleBillTerms }, setData };
};

export default useCalcTaxAmount;
